import React, { useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

// Usage in any MiniERP page:
//
//   import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
//   const { status, showSuccess, showError, showInfo } = useStatusBanner();
//
//   try {
//     await createLedger(formData);
//     showSuccess('Ledger created successfully!');
//   } catch (err) {
//     showError(err.message);
//   }
//
//   Then render <StatusBanner status={status} /> immediately above your
//   action buttons (inside the modal/form, right before the Cancel/Submit row).
//
// Auto-dismisses after 4s; also dismissable by the × button.

const STYLES = {
  success: {
    wrap: 'bg-emerald-950/60 border border-emerald-600 text-emerald-300',
    icon: faCheckCircle,
  },
  error: {
    wrap: 'bg-rose-950/60 border border-rose-600 text-rose-300',
    icon: faExclamationTriangle,
  },
  info: {
    wrap: 'bg-cyan-950/60 border border-cyan-600 text-cyan-300',
    icon: faInfoCircle,
  },
};

export function useStatusBanner() {
  const [status, setStatus] = useState(null); // { type: 'success'|'error'|'info', message }
  const timerRef = useRef(null);

  const show = useCallback((type, message, autoDismissMs = 4000) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus({ type, message });
    if (autoDismissMs) {
      timerRef.current = setTimeout(() => setStatus(null), autoDismissMs);
    }
  }, []);

  const showSuccess = useCallback((message, ms) => show('success', message, ms), [show]);
  const showError = useCallback((message, ms) => show('error', message ?? 'Something went wrong', ms), [show]);
  const showInfo = useCallback((message, ms) => show('info', message, ms), [show]);
  const clear = useCallback(() => setStatus(null), []);

  return { status, showSuccess, showError, showInfo, clear };
}

export const StatusBanner = ({ status, className = '' }) => {
  if (!status) return null;
  const style = STYLES[status.type] || STYLES.info;

  return (
    <div className={`flex items-start gap-2 rounded-xl px-4 py-2 text-sm ${style.wrap} ${className}`}>
      <FontAwesomeIcon icon={style.icon} className="mt-0.5 shrink-0" />
      <span className="flex-1">{status.message}</span>
    </div>
  );
};
